/**
 * Ops screen (SKBRIDGE-CC-003 CH-U1) — platform-ops only, three panels:
 * Connections · Egress audit (the ADR-001 answer as a screen) · Failure
 * visibility. Same design grammar as the directory: Plex Sans prose, Plex Mono
 * for ids/connectors/latencies, semantic tones, no dead controls.
 */
import React from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { useBridge } from '../../store/bridgeStore';
import ConnectionsPanel from './ConnectionsPanel';
import EgressAuditPanel from './EgressAuditPanel';
import FailuresPanel from './FailuresPanel';

const PANELS = [
  { key: 'connections', label: 'Connections' },
  { key: 'audit', label: 'Egress audit' },
  { key: 'failures', label: 'Failure visibility' },
] as const;

export type OpsPanel = (typeof PANELS)[number]['key'];

export default function OpsScreen() {
  const { state } = useBridge();
  const [panel, setPanel] = React.useState<OpsPanel>('connections');

  // Platform-ops only. Never a blank screen — say why the surface is closed.
  if (state.role !== 'softknack') {
    return (
      <View style={styles.gate}>
        <Text style={styles.gateTitle}>Platform operators only</Text>
        <Text style={styles.gateBody}>
          The Bridge ops surface shows every tenant's egress. Switch to the Softknack admin role to open it.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <View style={styles.tabs}>
        {PANELS.map((p) => (
          <Pressable key={p.key} onPress={() => setPanel(p.key)} style={[styles.tab, panel === p.key && styles.tabActive]}>
            <Text style={[styles.tabText, panel === p.key && styles.tabTextActive]}>{p.label}</Text>
          </Pressable>
        ))}
      </View>
      <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
        {panel === 'connections' && <ConnectionsPanel />}
        {panel === 'audit' && <EgressAuditPanel />}
        {panel === 'failures' && <FailuresPanel />}
      </ScrollView>
    </View>
  );
}

// Shared panel furniture — skeleton rows (never spinners over blank), honest
// empty states, and the mono cell used for ids/connectors/latencies.
export function PanelFrame({
  title,
  subtitle,
  toolbar,
  children,
}: {
  title: string;
  subtitle: string;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.panel}>
      <View style={styles.panelHead}>
        <View style={{ flex: 1 }}>
          <Text style={styles.panelTitle}>{title}</Text>
          <Text style={styles.panelSub}>{subtitle}</Text>
        </View>
      </View>
      {toolbar ? <View style={styles.toolbar}>{toolbar}</View> : null}
      {children}
    </View>
  );
}

export function Mono({ children, dim }: { children: React.ReactNode; dim?: boolean }) {
  return <Text style={[styles.mono, dim && { color: colors.ink3 }]}>{children}</Text>;
}

export function EmptyState({ text }: { text: string }) {
  return (
    <View style={styles.empty}>
      <Text style={styles.emptyText}>{text}</Text>
    </View>
  );
}

export function Loading() {
  return (
    <View style={styles.empty}>
      <ActivityIndicator size="small" color={colors.ink3 as string} />
    </View>
  );
}

export function ErrorNote({ text }: { text: string }) {
  return (
    <View style={styles.errorNote}>
      <Text style={styles.errorNoteText}>{text}</Text>
    </View>
  );
}

// Relative age, with the absolute timestamp always available alongside.
export function age(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.paper },
  tabs: {
    flexDirection: 'row',
    gap: 4,
    paddingHorizontal: 20,
    paddingTop: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
  },
  tab: { paddingVertical: 8, paddingHorizontal: 14, borderBottomWidth: 2, borderBottomColor: 'transparent', cursor: 'pointer' } as any,
  tabActive: { borderBottomColor: colors.ink },
  tabText: { fontFamily: fontFamily.sans, fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.ink3 },
  tabTextActive: { color: colors.ink },

  body: { flex: 1 },
  bodyContent: { padding: 20, gap: 16 },

  panel: { gap: 12 },
  panelHead: { flexDirection: 'row', alignItems: 'flex-start' },
  panelTitle: { fontFamily: fontFamily.sans, fontSize: fontSize.xl, fontWeight: fontWeight.semibold, color: colors.ink },
  panelSub: { fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.ink3, marginTop: 2 },
  toolbar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },

  mono: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },

  empty: {
    padding: 28,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    backgroundColor: colors.card,
  },
  emptyText: { fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.ink3 },

  errorNote: {
    padding: 12,
    borderWidth: 1,
    borderColor: colors.dangerLine,
    backgroundColor: colors.dangerSoft,
    borderRadius: radii.r2,
  },
  errorNoteText: { fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.danger },

  gate: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40, gap: 6, backgroundColor: colors.paper },
  gateTitle: { fontFamily: fontFamily.sans, fontSize: fontSize.xl, fontWeight: fontWeight.semibold, color: colors.ink },
  gateBody: { fontFamily: fontFamily.sans, fontSize: fontSize.md, color: colors.ink3, textAlign: 'center', maxWidth: 460 },
});
